import type * as apiClient from '@mogul/api-client';
import { Configuration } from '@mogul/api-client';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';
import type { useQuery, UseQueryOptions, UseQueryResult } from 'react-query';
import * as r from 'runtypes';

export const isAxiosError = (
  error: unknown,
): error is AxiosError<unknown> & { response: AxiosResponse<unknown> } => {
  return (
    error instanceof Error &&
    (error as AxiosError).isAxiosError &&
    !!(error as AxiosError).response
  );
};

export const parseApiErrors = (error: unknown) => {
  if (
    isAxiosError(error) &&
    r.Record({ errors: r.Array(r.Unknown) }).guard(error.response.data)
  ) {
    return error.response.data.errors.filter(
      // eslint-disable-next-line @typescript-eslint/unbound-method
      r.Record({
        code: r.String.optional(),
        title: r.String.optional(),
        detail: r.String.optional(),
        pointer: r.String.optional(),
        meta: r.Dictionary(r.Unknown, r.String).optional(),
      }).guard,
    );
  }

  return [];
};

export const parseValidationErrors = (error: unknown) => {
  const errors: any = {};

  for (const { code, meta, pointer } of parseApiErrors(error)) {
    if (code?.startsWith('validation.') && pointer?.startsWith('/body/')) {
      const parts = pointer.split('/').slice(2);

      if (typeof meta?.missingProperty === 'string') {
        parts.push(meta.missingProperty);
      }

      let current = errors;

      for (let i = 0; i < parts.length - 1; i++) {
        if (current[parts[i]] === undefined) {
          current[parts[i]] = !isNaN(parseInt(parts[i + 1], 10)) ? [] : {};
        }
        current = current[parts[i]];
      }

      current[parts[parts.length - 1]] = code.replace(/^validation\./, '');
    }
  }

  return errors;
};

export const bindApiConfig =
  <T>(
    defaults: apiClient.ConfigurationParameters,
    Constructor: new (configuration?: Configuration) => T,
  ) =>
  (_config?: apiClient.ConfigurationParameters) =>
    new Constructor(new Configuration({ ...defaults, ..._config }));

const capitalize = <T extends string>(str: T) =>
  (str.charAt(0).toUpperCase() + str.slice(1)) as Capitalize<T>;

export type UseApiQuery<TFn, TParams, TQueryFnData> = <TData = TQueryFnData>(
  requestParameters: TParams,
  options?: Omit<
    UseQueryOptions<TQueryFnData, unknown, TData, [TFn, TParams]>,
    'queryKey' | 'queryfn'
  >,
) => UseQueryResult<TData>;

export type ApiQueryHooks<T> = {
  [TFn in keyof T as `use${Capitalize<string & TFn>}`]: UseApiQuery<
    TFn,
    T[TFn] extends (requestParameters: infer TParams) => any ? TParams : never,
    T[TFn] extends (...args: any[]) => Promise<AxiosResponse<infer TData>>
      ? TData
      : never
  >;
};

export const createQueryHooks = <
  T extends InstanceType<
    typeof apiClient[keyof typeof apiClient & `${string}Api`]
  >,
>(
  _useQuery: typeof useQuery,
  api: T,
) => {
  const hooks: Partial<ApiQueryHooks<T>> = {};

  Object.getOwnPropertyNames(api.constructor.prototype).forEach(key => {
    const method = key as string & keyof T;

    if (typeof api[method] === 'function') {
      const hook = `use${capitalize(method)}` as const;

      /** @ts-expect-error */
      hooks[hook] = (requestParameters, options) =>
        _useQuery({
          queryKey: [hook, requestParameters],
          ...options,
          queryFn: () => {
            const source = axios.CancelToken.source();

            const fn = api[method] as any as (
              params: typeof requestParameters,
              config: AxiosRequestConfig,
            ) => Promise<AxiosResponse>;

            const promise = fn
              .call(api, requestParameters, { cancelToken: source.token })
              // eslint-disable-next-line @typescript-eslint/no-unsafe-return
              .then(response => response.data);

            (promise as any).cancel = () => {
              source.cancel('Query was cancelled by React Query');
            };

            return promise;
          },
        });
    }
  });

  return hooks as any as ApiQueryHooks<T>;
};
